import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import classNames from 'classnames'
import { NavigationBar } from '../components/NavigationBar'
import { AppHeader } from '../components/ActionBar'
import { NotificationHost } from '../components/Notifications/NotificationHost'
import { useGetUserSettings, useUserSettingsStore, type AuthState } from '@zendia/frontend-shared'
import { useGetLabels, useGetProjects } from '@zendia/frontend-shared'
import { axiosRequest } from '../utilities/axiosClient'
import { isOnboardingIncomplete } from '../constants/onboarding.constants'
import { useContext, useEffect } from 'react'
import { useToolbarCollapse } from '../hooks/useToolbarCollapse'
import { ThemeContext } from '../hooks/useTheme'

export const Route = createFileRoute('/_authenticated')({
  component: AuthenticatedContainer,
  beforeLoad: async (ctx) => {
    // Ensure user is authenticated before loading the route
    const authToken = (ctx.context?.auth as AuthState)?.authToken
    if (!authToken) {
      console.log('No auth token found, redirecting to login')
      throw redirect({
        to: '/login',
        search: {
          redirect: ctx.location.href,
        },
      })
    }
  },
})

function AuthenticatedContainer() {
  // Initialize data stores
  // Fetch labels for the authenticated user
  useGetLabels(axiosRequest)
  useGetProjects(axiosRequest)
  const { isSuccess: hasLoadedUserSettings } = useGetUserSettings(axiosRequest)

  const navigate = Route.useNavigate()

  const { settings: userSettings } = useUserSettingsStore()
  const { isExpanded, toggleExpanded } = useToolbarCollapse()
  const { setTheme } = useContext(ThemeContext)

  useEffect(() => {
    // The settings request is itself authenticated, so until it resolves we have no
    // idea how far through onboarding the user is. Redirecting on the empty store
    // would send every authenticated user to onboarding before they can be read.
    if (!hasLoadedUserSettings) return

    if (isOnboardingIncomplete(userSettings?.onboardingStep)) {
      console.log('User onboarding incomplete, redirecting to onboarding')
      navigate({ to: '/onboarding' })
    }
    // Onboarding is done, so the requested authenticated route is rendered as-is.
    // Sending everyone to the dashboard here would break every other deep link.
  }, [hasLoadedUserSettings, userSettings, navigate])

  useEffect(() => {
    // Apply the server-stored Appearance choice once settings have loaded. A stored
    // 'light'/'dark' is an explicit override; 'system' (or never set) defers back to
    // prefers-color-scheme, same as a brand-new browser. This is what makes the choice
    // follow the user across devices/browsers rather than living only in this browser's
    // localStorage - the Settings picker already wrote there, but only here.
    //
    // The account is the source of truth and localStorage only this browser's cache of it,
    // so an account with no stored preference is reset to 'system' rather than left running
    // on whatever is cached. Without that, a pick made in an older build - which wrote
    // localStorage and nothing else - stayed in force forever, overriding the browser's own
    // appearance setting with a choice made in some long-finished session.
    if (!hasLoadedUserSettings) return

    const preference = userSettings?.themePreference
    setTheme(preference === 'light' || preference === 'dark' ? preference : undefined)
  }, [hasLoadedUserSettings, userSettings?.themePreference, setTheme])

  return (
    <div className="flex flex-col sm:flex-row h-dvh relative">
      {/* The header sits inside this scroll container rather than pinned above it, so it scrolls
          away with the content - the account menu it carries goes with it. Only this column
          scrolls, which is what keeps the nav beside it in place without either element being
          fixed. overflow-y-auto (not overflow-scroll, which reserves both bars unconditionally
          wherever scrollbars are classic rather than overlay) shows a bar only when there is
          something to scroll, and min-w-0 overrides the flex item default of min-width:auto so
          wide content shrinks to the column instead of pushing it past the viewport. */}
      <div className="flex flex-col flex-1 min-w-0 sm:order-2 overflow-y-auto relative">
        <AppHeader />
        <div className="flex-1 sm:p-2 md:p-4">
          <Outlet />
        </div>
      </div>
      <div
        className={classNames(
          'flex-none bg-card dark:bg-card-dark border-t border-gray-300/50 dark:border-gray-600/50',
          'sm:order-1 sm:py-4 sm:flex sm:flex-col transition-[width] duration-200',
          'sm:border-e sm:border-t-0 sm:border-gray-300/50 sm:dark:border-gray-600/50',
          // Collapsed, the rail is only 80px wide, so the generous side padding it carries
          // when expanded would leave nothing for the 24px icons - they'd shrink to zero.
          isExpanded ? 'sm:w-54 sm:px-2' : 'sm:w-20 sm:px-0',
        )}
      >
        <NavigationBar isExpanded={isExpanded} toggleExpanded={toggleExpanded} />
      </div>
      <NotificationHost />
    </div>
  )
}
